/**
 * Path: packages/frontend/src/router/index.ts
 * Task: Main application router. Synchronizes URL parameters with workspaceStore, filterStore, and uiStore.
 * Update: Added automatic 'Select All' default filter for Web Map and Sequence Map while keeping Browser clean.
 */
import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import { useUiStore } from '@/store/ui';
import { useWorkspaceStore } from '@/store/workspace';
import { useFilterStore } from '@/store/filters';
import { useProjectStore } from '@/store/project';
import { useDatastoreStore } from '@/store/datastore';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/features/home/HomeView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/project/:projectId/dashboard',
      name: 'dashboard',
      component: () => import('@/features/dashboard/DashboardView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/project/:projectId/editor',
      name: 'editor',
      component: () => import('@/features/browser/BrowserView.vue'),
      meta: { requiresAuth: false }
    },
    // Backwards-compatibility redirect for legacy bookmarks (e.g. /editor/roles/ROL-1)
    {
      path: '/project/:projectId/editor/:legacyType/:legacyId?',
      redirect: (to) => ({
        name: 'editor',
        params: { projectId: to.params.projectId },
        query: {
          ...(to.params.legacyId ? { entity: to.params.legacyId } : {}),
          ...(to.params.legacyType ? { type: to.params.legacyType } : {})
        }
      })
    },
    // --- MAPS ---
    {
      path: '/project/:projectId/maps-web',
      name: 'mapWeb',
      component: () => import('@/features/maps/webMap/WebMapView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/project/:projectId/maps-sequence',
      name: 'mapSequence',
      component: () => import('@/features/maps/sequenceMap/SequenceMapView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/project/:projectId/maps-impactMap',
      name: 'impactMap',
      component: () => import('@/features/maps/impactMap/ImpactMapView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/project/:projectId/maps-star',
      name: 'mapStar',
      component: () => import('@/features/maps/starMap/StarMapView.vue'),
      meta: { requiresAuth: false }
    },
    // --- REPORTS ---
    {
      path: '/project/:projectId/reports-kanban',
      name: 'kanban',
      component: () => import('@/features/reports/kanban/KanbanView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/project/:projectId/reports-matrix',
      name: 'matrix',
      component: () => import('@/features/reports/matrix/MatrixView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/project/:projectId/reports-ishikawa',
      name: 'mapIshikawa',
      component: () => import('@/features/reports/ishikawa/IshikawaView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/project/:projectId/reports-calendar',
      name: 'calendar',
      component: () => import('@/features/reports/calendar/CalendarView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/project/:projectId/reports-roles',
      name: 'roledescription',
      component: () => import('@/features/reports/roleProfile/RoleProfileView.vue'),
      meta: { requiresAuth: false }
    },
    // --- SETTINGS ---
    {
      path: '/project/:projectId/settings-project',
      name: 'customize',
      component: () => import('@/features/projectSettings/ProjectSettingsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/appSettings/settings-projects',
      name: 'projectAdministration',
      component: () => import('@/features/globalSettings/projectAdministration/ProjectAdministrationView.vue'),
      meta: { requiresAuth: true, requiresAppAdmin: true }
    },
    {
      path: '/appSettings/settings-users',
      name: 'userSettings',
      component: () => import('@/features/globalSettings/userSettings/UserSettings.vue'),
      meta: { requiresAuth: true, requiresAppAdmin: true }
    },
    {
      path: '/appSettings/settings-data',
      name: 'dataSettings',
      component: () => import('@/features/globalSettings/dataSettings/DataSettingsView.vue'),
      meta: { requiresAuth: true, requiresAppAdmin: true }
    },
    // Catch-all route for 404s
    {
      path: '/:pathMatch(.*)*',
      redirect: '/'
    }
  ]
});

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  const uiStore = useUiStore();
  const workspaceStore = useWorkspaceStore();
  const filterStore = useFilterStore();
  const projectStore = useProjectStore();
  const datastoreStore = useDatastoreStore();

  // 1. Ensure user session is validated before checking routes
  if (!authStore.appData) {
    await authStore.fetchAppData();
  }
  if (!authStore.user && authStore.isLoading === false) {
    await authStore.checkSession();
  }

  // 2. Authentication and Authorization Check
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    authStore.showNotification('Please log in to access this page.', 'warning');
    authStore.isLoginDialogOpen = true;
    return next({ name: 'home' });
  }

  if (to.meta.requiresAppAdmin && !authStore.isAppAdmin) {
    authStore.showNotification('You do not have permission to access this area.', 'error');
    return next({ name: 'home' });
  }

  // 3. Global Project Data Hydration
  const projectId = to.params.projectId as string | undefined;
  if (projectId) {
    if (projectStore.loadedProject?.value !== projectId) {
      const title = projectStore.projectsMetadata?.[projectId]?.displayName || projectId;
      projectStore.setLoadedProject({ value: projectId, title });
    }

    if ((!datastoreStore.data || datastoreStore.activeProjectId !== projectId) && !datastoreStore.isLoading) {
      if (datastoreStore.activeProjectId !== projectId) {
        datastoreStore.activeProjectId = null;
        datastoreStore.data = null;
        workspaceStore.setSelectedEntity(null);
        workspaceStore.setSecondarySelectedEntity(null);
      }
      datastoreStore.fetchProjectDatastore(projectId).catch(console.error);
    }

    if (Object.keys(projectStore.projectsMetadata).length === 0 && !projectStore.isLoading) {
      projectStore.fetchProjectsMetadata().catch(console.error);
    }
  }

  // 4. View Context & Filter Synchronization
  if (to.name === 'editor') {
    const entityId = (to.query.entity || to.params.entityId) as string | undefined;
    const explicitType = to.query.type as string | undefined;

    // Sync Entity Selection if deep linked via query
    if (entityId && workspaceStore.selectedEntityId !== entityId) {
      workspaceStore.setSelectedEntity(entityId);
    } else if (!entityId && workspaceStore.selectedEntityId && from.name !== 'editor') {
      workspaceStore.setSelectedEntity(null);
    }

    // Only set a type filter if explicitly requested via query parameter (e.g. from Dashboard click)
    if (explicitType) {
      const types = explicitType.split(',');
      if (filterStore.filterQuery.length > 0) {
        if (filterStore.filterQuery[0].types.join(',') !== types.join(',')) {
          filterStore.filterQuery[0].types = types;
        }
      } else {
        filterStore.filterQuery = [{ id: 'root', types, instances: [] }];
      }
      uiStore.panels.showFilters = true;
    } else if (!to.query.type && from.name !== 'editor') {
      // Navigated via Sidebar or direct link without type param: ensure NO filters are active in Browser!
      filterStore.filterQuery = [{ id: 'root', types: [], instances: [] }];
      filterStore.statusFilter = [];
      filterStore.textFilter = '';
      filterStore.dynamicFilters = {};
      uiStore.panels.showFilters = false;
    }
  } else if (to.name === 'mapWeb' || to.name === 'mapSequence') {
    // Navigating to Web Map or Sequence Map: pre-select ALL workflow types by default ('Select All')
    const workflowTypes = new Set<string>();
    (authStore.appData?.relations || []).forEach((rel: any) => {
      if (rel.semanticType === 'sequence') {
        if (rel.sourceTypes) rel.sourceTypes.forEach((t: string) => workflowTypes.add(t));
        if (rel.targetTypes) rel.targetTypes.forEach((t: string) => workflowTypes.add(t));
      }
    });
    const defaultTypes = Array.from(workflowTypes);
    filterStore.filterQuery = [{ id: 'root', types: defaultTypes, instances: [] }];
    filterStore.statusFilter = [];
    filterStore.textFilter = '';
    filterStore.dynamicFilters = {};
    uiStore.panels.showFilters = false;
  } else if (from.name === 'editor' && to.name !== 'editor') {
    // When leaving editor towards other views, clear any active filter constraints
    filterStore.filterQuery = [{ id: 'root', types: [], instances: [] }];
    filterStore.statusFilter = [];
    filterStore.textFilter = '';
    filterStore.dynamicFilters = {};
    uiStore.panels.showFilters = false;
  }

  // 5. Global UI State Update
  if (to.name && typeof to.name === 'string') {
    uiStore.activeView = to.name;
  }

  next();
});

export default router;
